import { useMemo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { forms } from 'constants/hospitalPrevention';
import DraggableModal from 'components/organisms/draggableModal';
import { useRunbookLog } from 'infrastructure/hooks/clinic/use-runbook-logs';
import Section from 'components/molecules/section';
import BaseButton from 'components/atoms/baseButton';

const HospitalPreventionFormModal = ({
  isOpen,
  closeModal,
  onSubmit,
  clinicGuid,
  defaultValue = {},
  submitLoading,
}) => {
  const { runbookLogs, loading } = useRunbookLog({
    meta: {
      clinicGuid,
      items: 1,
    },
  });
  const initialized = useRef(false);
  const [modalValue, setModalValue] = useState(defaultValue);
  const setValue = (value) => {
    setModalValue((prev) => ({ ...prev, ...value }));
  };

  useEffect(() => {
    if (runbookLogs?.items?.length > 0 && !initialized.current) {
      const notes = runbookLogs?.items[0]?.notes;
      const dateNoteLogged = runbookLogs?.items[0]?.dateNoteLogged;
      const patientContactMethod = runbookLogs?.items[0]?.contactMethod;
      const contactMethodValue = runbookLogs?.items[0]?.contactMethodValue;
      const criticalBusinessHours =
        runbookLogs?.items[0]?.criticalBusinessHours;
      const criticalAfterHours = runbookLogs?.items[0]?.criticalAfterHours;
      const afterHours = runbookLogs?.items[0]?.afterHours;
      const businessHours = runbookLogs?.items[0]?.businessHours;

      setModalValue({
        ...defaultValue,
        patientContactMethod,
        contactMethodValue,
        criticalBusinessHours,
        criticalAfterHours,
        afterHours,
        businessHours,
        notes,
        dateNoteLogged,
      });
      initialized.current = true;
    }
  }, [runbookLogs?.items]);

  const disableSaveButton = useMemo(() => {
    return forms.some(({ id, notRequiredIf }) => {
      let disabled = true;

      const value = modalValue[id];
      if (Array.isArray(value) && !value.length) {
        disabled = false;
      }
      if (!value) {
        disabled = false;
      }

      if (notRequiredIf) {
        Object.keys(modalValue).forEach((key) => {
          if (notRequiredIf.includes(key)) {
            const innerValue = modalValue[key];
            if (Array.isArray(innerValue) && innerValue.length) {
              disabled = true;
            }
            if (!Array.isArray(innerValue) && innerValue) {
              disabled = true;
            }
          }
        });
      }
      return !disabled;
    });
  }, [modalValue]);
  const submit = () => {
    const values = {};
    Object.keys(modalValue).forEach((key) => {
      if (Array.isArray(modalValue[key])) {
        values[key] = modalValue[key].map((item) => item.value);
      } else {
        values[key] = modalValue[key];
      }
    });
    onSubmit(values);
  };

  return (
    <DraggableModal
      isOpen={isOpen}
      toggle={closeModal}
      title="Hospitalization Prevention"
      footer={
        <BaseButton
          onClick={submit}
          disabled={disableSaveButton || submitLoading || loading}
          label={submitLoading ? 'Saving...' : 'Save'}
        />
      }
    >
      <div className="narrative-popup">
        <div
          className="input-wrapper"
          style={{
            margin: '0 6px',
          }}
        >
          <span>
            Please document any instance where Accuhealth prevented a
            hospitalization while following our Alert Process.
          </span>
          <br />
          <br />
          <span>
            This form will report incidents in the following format: Risk For
            ______, evidenced by _________.
          </span>
          <br />
          <strong>
            ex: "risk for infection, evidenced by inadequate vaccination and
            immunosuppression"
          </strong>
        </div>
        <Section forms={forms} setValue={setValue} values={modalValue} />
      </div>
    </DraggableModal>
  );
};

HospitalPreventionFormModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
  onSubmit: PropTypes.func,
  defaultValue: PropTypes.object,
  clinicGuid: PropTypes.string,
  submitLoading: PropTypes.bool,
};

export default HospitalPreventionFormModal;
